'use client'

import { useEffect, useRef, useState } from 'react'

import {
  faCircleNotch,
  faHeart,
  faInfoCircle,
  faTriangleExclamation,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons'
import {
  faBolt,
  faGavel,
  faHeart as faHeartSolid,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQuery } from '@tanstack/react-query'
import { useIntl } from 'react-intl'
import { css, keyframes, styled } from 'styled-components'

import { RoleStatus } from '@b-stock/account-api-client'
import {
  useAccessToken,
  useAccessTokenRoles,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import {
  getAuctionWithBid,
  getSelectedQuoteQuery,
  myLocationsQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  FormattedMessage,
  Button,
  ButtonAsA,
  Chip,
  Modal,
  ProgressLine,
} from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'
import { LocationAttribute } from '@b-stock/location-api-client'

import { calculateFeePercent } from '@/buyer-portal/components/features/listing/hooks/calculateFeePercent'
import { useCalculatedAdditionalCharges } from '@/buyer-portal/components/features/listing/hooks/useCalculatedAdditionalCharges'
import { useTlcTotals } from '@/buyer-portal/components/features/listing/hooks/useTlcTotals'
import { useActivePhase } from '@/buyer-portal/helpers/auctions/buyingFormat'
import { TlcBreakdown } from '@/components/common/TlcBreakdown'
import useToggleWatchlisting from '@/components/listings/watchlist/useToggleWatchlisting'
import { getWatchlistDateExpires } from '@/helpers/dateUtils'
import { useListingViewTracking } from '@/helpers/telemetry/ListingViewTrackingProvider'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { violatesBidPrecision } from '@/helpers/violatesBidPrecision'
import type { SearchResult } from '@/queries/listingSearchQuery'
import manifestSummaryQuery from '@/queries/manifestSummaryQuery'
import { watchedStatusQuery } from '@/queries/watchlistQuery'

import usePopupAutoQuoteGeneration from './usePopupAutoQuoteGeneration'

const LOADING_STAGES = [
  { messageId: 'ManifestSummaryPopup.loadingStage1', progress: 20 },
  { messageId: 'ManifestSummaryPopup.loadingStage2', progress: 40 },
  { messageId: 'ManifestSummaryPopup.loadingStage3', progress: 60 },
  { messageId: 'ManifestSummaryPopup.loadingStage4', progress: 80 },
  { messageId: 'ManifestSummaryPopup.loadingStage5', progress: 100 },
] as const

const STAGE_INTERVAL_MS = 800

// Reuses BidActionCard's Buy Now TLC copy so the two surfaces stay in sync.
const tlcNs = 'Listing.actionColumn.actionCard.bid'

// ── Modal shell ───────────────────────────────────────────────────────────────

const slideUp = keyframes`
  from { transform: translateY(100%); }
`

const SidebarModal = styled(Modal)`
  width: clamp(26rem, 30vw, 42rem);

  @media ${Breakpoints.max.small} {
    /* Override sidebar (right-side panel) to bottom sheet on mobile */
    position: absolute;
    top: unset;
    right: unset;
    left: 0;
    bottom: 0;
    width: 100%;
    max-height: 90vh;
    border-radius: 0.75rem 0.75rem 0 0;
    animation: ${css`
        ${slideUp}
      `}
      0.3s ease-out;
  }
`

const SidebarContent = styled(Modal.Content)`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  /* Scrolling is handled by ModalContainer (overflow: auto). Keeping overflow-y: auto
     here clips absolutely-positioned tooltips inside TlcBreakdown. */
  overflow: visible;
`

// ── Header ────────────────────────────────────────────────────────────────────

const PanelHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 1rem 1rem 0.75rem;
  border-bottom: 1px solid ${Colors.Semantic.Neutral.medium_grey};
  flex-shrink: 0;
`

const HeaderTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
`

const HeaderLabel = styled.span`
  ${Typography.Overline2}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
`

const HeaderSubtitle = styled.p`
  ${Typography.Body2}
  color: ${Colors.Semantic.Neutral.dark_grey};
  margin: 0;
`

const CloseButton = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  flex-shrink: 0;
  color: ${Colors.Semantic.Neutral.dark_grey};

  &:hover {
    color: ${Colors.Semantic.Neutral.very_dark_grey};
  }
`

// ── TLC ───────────────────────────────────────────────────────────────────────

const TlcSection = styled.div`
  padding-bottom: 0.75rem;

  /* TlcBreakdown was designed for wider containers. Keep the price/chevron
     from being pushed off in the narrow popup sidebar. */
  button[aria-expanded] > div:first-child {
    min-width: 0;
  }
  button[aria-expanded] > div:last-child {
    flex-shrink: 0;
  }

  /* Container uses overflow:hidden for border-radius, which clips the
     absolutely-positioned tooltip. Keep the radius via direct property. */
  & > div {
    overflow: visible;
    border-radius: 0.5rem;
  }

  /* Constrain tooltip width so @floating-ui's shift keeps it within the
     26rem popup without clipping at the ModalContainer boundary. */
  [role='tooltip'] {
    max-width: 22rem;
  }
`

const ShippingOnlyRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: ${Colors.Semantic.Neutral.light_grey};
  border-radius: 0.5rem;
  ${Typography.Body3}
`

const ShippingOnlyLabel = styled.span`
  color: ${Colors.Semantic.Neutral.dark_grey};
`

const ShippingOnlyValue = styled.span`
  ${Typography.Body3_Semibold}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
`

// ── Listing title block ───────────────────────────────────────────────────────

const StorefrontLabel = styled.div`
  ${Typography.Overline2}
  color: ${Colors.Semantic.Neutral.dark_grey};
  margin-bottom: 0.25rem;
`

const ListingTitle = styled.h2`
  ${Typography.Title3}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  margin: 0 0 0.5rem;
`

const StatsLine = styled.div`
  ${Typography.Body2}
  color: ${Colors.Semantic.Neutral.dark_grey};
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  align-items: center;
`

const StatDot = styled.span`
  color: ${Colors.Semantic.Neutral.medium_grey};
`

// ── Section headings ──────────────────────────────────────────────────────────

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid ${Colors.Semantic.Neutral.light_grey};

  &:last-of-type {
    border-bottom: none;
    padding-bottom: 0;
  }
`

const SectionLabel = styled.h3`
  ${Typography.Overline2}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  margin: 0;
  padding-bottom: 0.25rem;
  border-bottom: 1.5px solid ${Colors.Semantic.Neutral.very_dark_grey};
  align-self: flex-start;
`

// ── At a Glance ───────────────────────────────────────────────────────────────

const GlanceList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`

const GlanceItem = styled.li`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  margin-bottom: 0.375rem;

  &::before {
    content: '•';
    flex-shrink: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
`

// ── Top Retail Picks ──────────────────────────────────────────────────────────

const ResalePickList = styled.div`
  display: flex;
  flex-direction: column;
`

const ResalePickItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.625rem 0;
  border-bottom: 1px dashed ${Colors.Semantic.Neutral.light_grey};

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`

const ResalePickInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`

const ResalePickLabel = styled.span`
  ${Typography.Body3_Semibold}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
`

const ResalePickReason = styled.span`
  ${Typography.Body2}
  color: ${Colors.Semantic.Neutral.dark_grey};
  font-style: italic;
`

const ResalePickPrice = styled.span`
  ${Typography.Body3_Semibold}
  color: ${Colors.Semantic.Success.copy};
  white-space: nowrap;
  flex-shrink: 0;
`

// ── Bulk Items ────────────────────────────────────────────────────────────────

const BulkRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`

const BulkLabel = styled.span`
  ${Typography.Body3_Semibold}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  flex-shrink: 0;
  padding-top: 0.125rem;
`

const BulkChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  flex: 1;
  min-width: 0;
`

const FilledChip = styled(Chip)`
  background: ${Colors.Semantic.Neutral.light_grey};
  border-color: transparent;
`

// ── Disclaimer ────────────────────────────────────────────────────────────────

const DisclaimerBox = styled.div`
  background: ${Colors.Semantic.Info.background};
  border: 1px solid ${Colors.Semantic.Info.highlight};
  border-radius: 0.5rem;
  padding: 0.75rem;
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
`

const DisclaimerIcon = styled(FontAwesomeIcon)`
  color: ${Colors.Semantic.Info.highlight};
  flex-shrink: 0;
  margin-top: 0.125rem;
`

const DisclaimerText = styled.p`
  ${Typography.Body2}
  color: ${Colors.Semantic.Neutral.dark_grey};
  margin: 0;
`

// ── Loading state ─────────────────────────────────────────────────────────────

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`

const shimmer = keyframes`
  0%   { background-position: 200% center; }
  100% { background-position: -200% center; }
`

const LoadingHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 0.5rem;
`

const SpinnerIcon = styled(FontAwesomeIcon)`
  color: ${Colors.Semantic.Info.highlight};
  animation: ${css`
      ${spin}
    `}
    0.8s linear infinite;
`

const StageText = styled.span`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
`

const SkeletonLine = styled.div<{ $width?: string; $height?: string }>`
  width: ${({ $width }) => $width ?? '100%'};
  height: ${({ $height }) => $height ?? '0.875rem'};
  border-radius: 0.375rem;
  background: linear-gradient(
    90deg,
    ${Colors.Semantic.Neutral.light_grey} 25%,
    ${Colors.Semantic.Neutral.medium_grey} 50%,
    ${Colors.Semantic.Neutral.light_grey} 75%
  );
  background-size: 400% 100%;
  animation: ${css`
      ${shimmer}
    `}
    1.5s ease-in-out infinite;
`

const SkeletonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`

// ── Error state ───────────────────────────────────────────────────────────────

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(0.5rem); }
  to   { opacity: 1; transform: translateY(0); }
`

const iconPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.12); }
`

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2.5rem 1.5rem;
  text-align: center;
  animation: ${css`
      ${fadeSlideIn}
    `}
    0.35s ease both;
`

const ErrorIcon = styled(FontAwesomeIcon)`
  color: ${Colors.Semantic.Warning.highlight};
  animation: ${css`
      ${iconPulse}
    `}
    2s ease-in-out 0.35s infinite;
`

const ErrorMessage = styled.p`
  ${Typography.Body3_Semibold}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  margin: 0;
  line-height: 1.5;
`

// ── Bid / action bar ─────────────────────────────────────────────────────────

const ActionBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${Colors.Semantic.Neutral.light_grey};

  @media ${Breakpoints.max.small} {
    position: sticky;
    bottom: 0;
    background: ${Colors.Semantic.Neutral.white};
    padding: 0.75rem;
    margin: 0 -1rem -0.75rem;
    border-top: 1px solid ${Colors.Semantic.Neutral.medium_grey};
  }
`

const BidInfoLine = styled.p`
  ${Typography.Body2}
  color: ${Colors.Semantic.Neutral.dark_grey};
  margin: 0;
`

const PercentageBidNote = styled.p`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  margin: 0;
`

const BidRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
`

const BidInputWrapper = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: stretch;
  border: 1px solid ${Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.5rem;
  overflow: hidden;
  background: ${Colors.Semantic.Neutral.white};

  &:focus-within {
    border-color: ${Colors.Semantic.Info.onhover};
  }
`

const BidInputPrefix = styled.span`
  ${Typography.Body3}
  display: flex;
  align-items: center;
  padding: 0.5rem 0.625rem;
  background: ${Colors.Semantic.Neutral.light_grey};
  color: ${Colors.Semantic.Neutral.dark_grey};
  border-right: 1px solid ${Colors.Semantic.Neutral.medium_grey};
  flex-shrink: 0;
  -webkit-user-select: none;
  user-select: none;
`

const BidInputField = styled.input`
  ${Typography.Body3}
  flex: 1;
  min-width: 0;
  width: 0;
  box-sizing: border-box;
  padding: 0.5rem 0.75rem;
  border: none;
  outline: none;
  background: transparent;

  /* Hide number input spinners */
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &::placeholder {
    color: ${Colors.Semantic.Neutral.dark_grey};
  }
`

const BidInputError = styled.span`
  ${Typography.Body2}
  color: ${Colors.Semantic.Error.highlight};
`

const PlaceBidButton = styled(Button)`
  flex-shrink: 0;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.375rem;
`

const BottomButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
`

const WatchlistPopupButton = styled(Button)`
  flex: 1;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
`

const SeeListingButton = styled(ButtonAsA)`
  flex: 1;
  justify-content: center;
`

// ── Component ─────────────────────────────────────────────────────────────────

const WatchlistPopupCTA = ({
  listingId,
  endTime,
  isWatchlisted,
  currency,
}: {
  listingId: string
  endTime: string
  isWatchlisted: boolean
  currency?: string
}) => {
  const intl = useIntl()
  const { trackButtonClicked } = useAnalytics()
  const dateExpires = getWatchlistDateExpires(new Date(endTime))
  const { isCurrentlyWatchlisted, isPending, toggleWatchlistListing } =
    useToggleWatchlisting({
      listingId,
      dateExpires,
      isWatchlisted,
      currency,
      listingEndTime: endTime,
    })

  const handleWatchlistClick = () => {
    trackButtonClicked({
      buttonName: isCurrentlyWatchlisted
        ? 'manifest_remove_from_watchlist'
        : 'manifest_add_to_watchlist',
      listing_id: listingId,
      screenName: 'manifest_summary_popup',
    })
    void toggleWatchlistListing()
  }

  return (
    <WatchlistPopupButton
      type="button"
      appearance="secondary"
      size="medium"
      disabled={isPending}
      onClick={handleWatchlistClick}
    >
      <FontAwesomeIcon icon={isCurrentlyWatchlisted ? faHeartSolid : faHeart} />
      {isCurrentlyWatchlisted
        ? intl.formatMessage({ id: 'ManifestSummaryPopup.watchlisted' })
        : intl.formatMessage({ id: 'ManifestSummaryPopup.addToWatchlist' })}
    </WatchlistPopupButton>
  )
}

// useIntl()/useNumberFormatParts both format against the viewer's browser
// locale (BStockIntlProvider), and Intl.NumberFormat renders CAD as a bare
// "$" under en-CA (CAD is en-CA's home currency) rather than the
// disambiguating "CA$" en-US shows for the same amount. Bid amounts must
// read unambiguously for every viewer, so pin this formatting to 'en-US'.
const getBidCurrencySymbol = (currencyCode: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
  })
    .formatToParts(0)
    .find((part) => part.type === 'currency')?.value ?? currencyCode

const formatBidCurrency = (value: number, currencyCode: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: 0,
  }).format(value)

type ManifestSummaryPopupProps = {
  listing: SearchResult
  onClose: () => void
}

const ManifestSummaryPopup = ({
  listing,
  onClose,
}: ManifestSummaryPopupProps) => {
  const intl = useIntl()
  const { trackButtonClicked } = useAnalytics()
  const { trackListingView } = useListingViewTracking()
  const { data, isLoading, isError } = manifestSummaryQuery.useWithOpts(
    [listing.id],
    { enabled: !!listing.id }
  )

  const hasTrackedViewRef = useRef<string | null>(null)
  useEffect(() => {
    if (!listing.id) return
    if (hasTrackedViewRef.current === listing.id) return
    hasTrackedViewRef.current = listing.id
    trackListingView(listing.id)
  }, [listing.id, trackListingView])

  const [stageIndex, setStageIndex] = useState(0)

  const decodedToken = useDecodedAccessToken()
  const personId = decodedToken?.personId
  const accountId = decodedToken?.accountId
  const accessToken = useAccessToken()
  const isActiveBuyer = useAccessTokenRoles()?.buyer === RoleStatus.Active

  const isEnterpriseListing = listing.siteName !== 'TMP'
  const isContract =
    listing.listingType === 'CONTRACT' ||
    listing.pricingStrategy === 'MAKE_AN_OFFER'

  const { data: watchlistStatus } = watchedStatusQuery.useWithOpts(
    [{ personId, listingId: listing.listingId }],
    { enabled: !!personId && !!listing.listingId }
  )
  const isWatchlisted =
    watchlistStatus?.isWatchlisted ??
    listing.watchlistStatus?.isWatchlisted ??
    false

  const myLocations = myLocationsQuery.useWithOpts(
    [
      {
        attributes: [LocationAttribute.DefaultDeliverable],
        ...(accountId && { accountId: [accountId] }),
      },
    ],
    { enabled: !!personId }
  )
  const defaultLocation = myLocations.data?.locations[0]

  // Only an active buyer has SHIPMENT_QUOTE_READ; others 403 on read.
  const { data: selectedQuote, isFetched: isQuoteFetched } =
    getSelectedQuoteQuery.useWithOpts([listing.listingId, accountId], {
      enabled:
        !!personId &&
        isActiveBuyer &&
        (!!defaultLocation?.id || myLocations.isFetched),
    })

  const { isAutoGenerating } = usePopupAutoQuoteGeneration({
    listingId: listing.listingId,
    accountId,
    personId,
    isQuoteFetched,
    selectedQuote,
    defaultLocation,
    isLocationFetched: myLocations.isFetched,
  })

  const { data: entAuction, isFetched: isEntAuctionFetched } = useQuery({
    queryKey: ['entAuctionForPopup', listing.listingId, accountId],
    queryFn: () =>
      getAuctionWithBid({
        accessToken,
        accountId,
        listingId: listing.listingId,
      }),
    enabled:
      !!personId && isEnterpriseListing && !isContract && !!listing.listingId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  // Wait for manifest, initial quote fetch, AND any auto-generation before showing content.
  // For logged-out users the quote/generation path is skipped entirely.
  const isPopupLoading =
    isLoading ||
    // Only waits on the quote fetch settling if it will ever run: a disabled
    // query (non-active buyer) never resolves isFetched, which would
    // otherwise strand the popup in this loading state forever.
    (!!personId && isActiveBuyer && !isQuoteFetched) ||
    isAutoGenerating ||
    (isEnterpriseListing &&
      !isContract &&
      !!personId &&
      !!listing.listingId &&
      !isEntAuctionFetched)
  useEffect(() => {
    if (!isPopupLoading) {
      setStageIndex(0) // oxlint-disable-line react/set-state-in-effect
      return
    }
    if (stageIndex >= LOADING_STAGES.length - 1) return
    const id = setTimeout(
      () =>
        setStageIndex((prev) => Math.min(prev + 1, LOADING_STAGES.length - 1)),
      STAGE_INTERVAL_MS
    )
    return () => clearTimeout(id)
  }, [isPopupLoading, stageIndex])
  const currentStage = LOADING_STAGES[stageIndex] ?? LOADING_STAGES[4]

  const formatCurrency = (value: number) =>
    intl.formatNumber(value, {
      style: 'currency',
      currency: listing.currency || 'USD',
      maximumFractionDigits: 0,
    })
  const bidCurrencyCode = listing.currency || 'USD'
  const currencySymbol = getBidCurrencySymbol(bidCurrencyCode)
  // Retail-value figures (manifest retail total, resale pick prices, per-unit
  // price) must keep the same precision as the listing card's MSRP and the
  // full manifest's Unit Retail column -- formatCurrency's 0-decimal rounding
  // truncates e.g. $5,668.12 down to $5,668, or $2.41/unit down to $2/unit.
  const formatRetailCurrency = (value: number) =>
    intl.formatNumber(value, {
      style: 'currency',
      currency: listing.currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })

  const isAuction = !!(listing.auction3MP || listing.auctionEnt)
  const bidCount =
    // The optional chains below are deliberate runtime defense: auction3MP/auctionEnt
    // and numberOfBids can be absent on legacy/augmented data despite their types.
    /* oxlint-disable typescript/no-unnecessary-condition */
    listing.auction3MP?.numberOfBids ??
    listing.auctionEnt?.numberOfBids ??
    listing.numberOfBids ??
    0
  /* oxlint-enable typescript/no-unnecessary-condition */
  const currentBid =
    (listing.auction3MP?.winningBidAmount ??
      listing.auctionEnt?.winningBidAmount ??
      // The search index's winningBidAmount is unreliable when there are no
      // bids yet -- for a Buy Now listing it holds the Buy Now price, not a
      // real winning bid, so only trust it once a bid has actually landed.
      (bidCount > 0 ? Number(listing.winningBidAmount) : 0)) ||
    0
  const startPrice =
    listing.auction3MP?.startPrice ?? listing.auctionEnt?.startPrice ?? 0
  const buyNowPrice = listing.auction3MP?.buyNow?.price ?? null
  const isBuyNowPhase = useActivePhase(listing) === 'BINO'
  // Buy Now listings bid/win at the fixed Buy Now price, not the auction's
  // running nextMinBid -- matches the effective-price basis used elsewhere
  // (CombinedListingSearchResult.priceForListDerivedMetrics).
  const nextMinBid =
    isBuyNowPhase && buyNowPrice != null && buyNowPrice > 0
      ? buyNowPrice
      : (listing.auction3MP?.nextMinBidAmount ??
          entAuction?.nextMinBidAmount ??
          (currentBid > 0
            ? currentBid + (entAuction?.bidPrecision ?? 1)
            : 0)) ||
        startPrice
  const bidBasis = listing.bidBasis === 'RELATIVE' ? 'RELATIVE' : 'ABSOLUTE'
  const bidPrecision =
    listing.auction3MP?.bidPrecision ?? entAuction?.bidPrecision ?? 1

  const [bidInput, setBidInput] = useState(
    !isEnterpriseListing && nextMinBid > 0
      ? String(nextMinBid)
      : entAuction?.nextMinBidAmount
        ? String(entAuction.nextMinBidAmount)
        : ''
  )

  useEffect(() => {
    if (entAuction?.nextMinBidAmount) {
      setBidInput(String(entAuction.nextMinBidAmount)) // oxlint-disable-line react/set-state-in-effect
    }
  }, [entAuction?.nextMinBidAmount])
  const bidValue = parseFloat(bidInput)

  const charges = useCalculatedAdditionalCharges({
    buyersPremium: data?.buyersPremium ?? 0,
    currentBidDollars: bidValue || nextMinBid || currentBid || startPrice,
    selectedQuote: selectedQuote ?? null,
  })
  const { shippingCents, tlcTaxInfo, tlcVatInfo, totalLandedCostWithTax } =
    useTlcTotals({
      additionalCharges: charges,
      currency: listing.currency,
      listingId: listing.listingId,
      listingType: listing.listingType,
      selectedQuote,
    })
  const feePercent =
    charges.basis > 0
      ? calculateFeePercent(charges.bstockFee, charges.basis)
      : (data?.buyersPremium ?? 0)
  const isNullPremium = !!data && data.buyersPremium == null
  const showShippingOnly = isContract || isNullPremium || isEnterpriseListing

  const listingUrl = listing.auction3MP
    ? `/buy/listings/details/${listing.listingId}`
    : listing.auctionUrl || `/buy/listings/details/${listing.id}`
  const isBidPrecisionValid =
    isBuyNowPhase ||
    bidInput === '' ||
    !violatesBidPrecision(bidInput, bidPrecision)
  const isBidValid =
    !isNaN(bidValue) && bidValue >= nextMinBid && isBidPrecisionValid
  const showBidError =
    bidInput !== '' && !isNaN(bidValue) && bidValue < nextMinBid
  const showBidPrecisionError =
    bidInput !== '' &&
    !isNaN(bidValue) &&
    bidValue >= nextMinBid &&
    !isBidPrecisionValid

  const handlePlaceBid = () => {
    if (!personId) {
      const returnUrl = `${listingUrl}?bidAmount=${encodeURIComponent(bidInput)}`
      window.open(
        `/acct/signin?redirectAfterLogin=${encodeURIComponent(returnUrl)}`,
        '_blank'
      )
      return
    }
    if (!isBidValid) return
    trackButtonClicked({
      buttonName: 'manifest_place_bid',
      listing_id: listing.listingId,
      bid_amount: bidValue,
      screenName: 'manifest_summary_popup',
    })
    window.open(`${listingUrl}?bidAmount=${bidValue}`, '_blank')
  }

  const statsSegments: string[] = []
  if (data?.palletCount != null)
    statsSegments.push(
      intl.formatMessage(
        { id: 'ManifestSummaryPopup.palletsCount' },
        { count: data.palletCount }
      )
    )
  if (data?.units != null)
    statsSegments.push(
      intl.formatMessage(
        { id: 'ManifestSummaryPopup.unitsCount' },
        { count: intl.formatNumber(data.units) }
      )
    )
  if (data?.retailValue != null)
    statsSegments.push(
      intl.formatMessage(
        { id: 'ManifestSummaryPopup.retailTotal' },
        { value: formatRetailCurrency(data.retailValue) }
      )
    )
  // Matches the listing card's price-per-unit (currentPrice / listing.units,
  // see PriceDetails.tsx) rather than the manifest's retail-value estimate, so
  // the two surfaces never disagree on "price per unit" for the same listing.
  const costPerUnit =
    !!data && !!listing.units && currentBid > 0
      ? currentBid / listing.units
      : null
  if (costPerUnit != null)
    statsSegments.push(
      intl.formatMessage(
        { id: 'ManifestSummaryPopup.costPerUnitStat' },
        { value: formatRetailCurrency(costPerUnit) }
      )
    )

  return (
    <SidebarModal appearance="sidebar" closeModal={onClose}>
      <PanelHeader>
        <HeaderTopRow>
          <HeaderLabel>
            <FormattedMessage id="ManifestSummaryPopup.title" />
          </HeaderLabel>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faXmark} />
          </CloseButton>
        </HeaderTopRow>
        <HeaderSubtitle>
          <FormattedMessage id="ManifestSummaryPopup.subtitle" />
        </HeaderSubtitle>
      </PanelHeader>

      <SidebarContent>
        <div>
          <StorefrontLabel>{listing.storefrontName}</StorefrontLabel>
          <ListingTitle>{listing.title}</ListingTitle>
          {statsSegments.length > 0 && (
            <StatsLine>
              {statsSegments.map((seg, index) => (
                <span key={seg}>
                  {index > 0 && <StatDot>·</StatDot>} {seg}
                </span>
              ))}
            </StatsLine>
          )}
        </div>

        {!!personId && !!data && !isPopupLoading && !showShippingOnly && (
          <TlcSection>
            <TlcBreakdown
              basis={charges.basis}
              bstockFee={charges.bstockFee}
              feePercent={feePercent}
              shippingCents={shippingCents}
              isWithoutShipping={charges.isWithoutShipping}
              taxInfo={tlcTaxInfo}
              totalLandedCostWithTax={totalLandedCostWithTax}
              currency={listing.currency}
              vatInfo={tlcVatInfo}
              {...(isBuyNowPhase && {
                amountLabelId: `${tlcNs}.buyNow.tlcBreakdown.amount`,
                tooltipId: `${tlcNs}.buyNow.tlcBreakdown.tooltip`,
                tooltipWithTaxId: `${tlcNs}.buyNow.tlcBreakdown.tooltipWithTax`,
                tooltipWithVatId: `${tlcNs}.buyNow.tlcBreakdown.tooltipWithVat`,
              })}
            />
          </TlcSection>
        )}

        {!!personId && !!data && !isPopupLoading && showShippingOnly && (
          <TlcSection>
            <ShippingOnlyRow>
              <ShippingOnlyLabel>
                <FormattedMessage id="ManifestSummaryPopup.estimatedShipping" />
              </ShippingOnlyLabel>
              <ShippingOnlyValue>
                {charges.isWithoutShipping
                  ? '—'
                  : formatCurrency(shippingCents / 100)}
              </ShippingOnlyValue>
            </ShippingOnlyRow>
          </TlcSection>
        )}

        {isPopupLoading && (
          <div>
            <LoadingHeaderRow>
              <SpinnerIcon icon={faCircleNotch} />
              <StageText>
                {intl.formatMessage({ id: currentStage.messageId })}
              </StageText>
            </LoadingHeaderRow>
            <ProgressLine percent={currentStage.progress} />
            <SkeletonGroup style={{ marginTop: '1.25rem' }}>
              <SkeletonLine $width="55%" />
              <SkeletonLine $width="80%" />
            </SkeletonGroup>
            <SkeletonGroup style={{ marginTop: '1rem' }}>
              <SkeletonLine $width="90%" />
              <SkeletonLine $width="75%" />
              <SkeletonLine $width="85%" />
              <SkeletonLine $width="60%" />
            </SkeletonGroup>
            <SkeletonLine $height="5.5rem" style={{ marginTop: '1rem' }} />
            <SkeletonLine $width="40%" style={{ marginTop: '0.75rem' }} />
          </div>
        )}

        {isError && (
          <ErrorState>
            <ErrorIcon icon={faTriangleExclamation} size="2x" />
            <ErrorMessage>
              <FormattedMessage id="ManifestSummaryPopup.error" />
            </ErrorMessage>
          </ErrorState>
        )}

        {data && !isPopupLoading && (
          <>
            {data.summary && data.summary.length > 0 && (
              <Section>
                <SectionLabel>
                  <FormattedMessage id="ManifestSummaryPopup.atAGlance" />
                </SectionLabel>
                <GlanceList>
                  {data.summary.map((item) => (
                    <GlanceItem key={item}>{item}</GlanceItem>
                  ))}
                </GlanceList>
              </Section>
            )}

            {data.topResalePicks && data.topResalePicks.length > 0 && (
              <Section>
                <SectionLabel>
                  <FormattedMessage id="ManifestSummaryPopup.topResalePicks" />
                </SectionLabel>
                <ResalePickList>
                  {data.topResalePicks.map((pick) => (
                    <ResalePickItem
                      key={`${pick.itemDescription ?? ''}-${pick.reason ?? ''}`}
                    >
                      <ResalePickInfo>
                        <ResalePickLabel>
                          {pick.itemDescription}
                        </ResalePickLabel>
                        {pick.reason && (
                          <ResalePickReason>{pick.reason}</ResalePickReason>
                        )}
                      </ResalePickInfo>
                      {pick.itemPrice != null && (
                        <ResalePickPrice>
                          {formatRetailCurrency(pick.itemPrice)}
                        </ResalePickPrice>
                      )}
                    </ResalePickItem>
                  ))}
                </ResalePickList>
              </Section>
            )}

            {data.bulkBrands && data.bulkBrands.length > 0 && (
              <Section>
                <BulkRow>
                  <BulkLabel>
                    <FormattedMessage id="ManifestSummaryPopup.bulkItems" />:
                  </BulkLabel>
                  <BulkChips>
                    {data.bulkBrands.map((item) => (
                      <FilledChip
                        key={`${item.brand ?? ''}-${item.unitCount ?? ''}`}
                        size="small"
                      >
                        {[
                          item.brand,
                          item.unitCount != null
                            ? `× ~${item.unitCount}`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      </FilledChip>
                    ))}
                  </BulkChips>
                </BulkRow>
              </Section>
            )}

            {data.disclaimer && (
              <DisclaimerBox>
                <DisclaimerIcon icon={faInfoCircle} size="sm" />
                <DisclaimerText>{data.disclaimer}</DisclaimerText>
              </DisclaimerBox>
            )}
          </>
        )}

        {(isAuction || !!listing.listingId) && (
          <ActionBar>
            {isAuction &&
              (!isEnterpriseListing || !personId || isEntAuctionFetched) && (
                <>
                  {!isBuyNowPhase && (
                    <BidInfoLine>
                      {intl.formatMessage(
                        { id: 'ManifestSummaryPopup.bidInfo' },
                        {
                          currentBid: formatBidCurrency(
                            currentBid,
                            bidCurrencyCode
                          ),
                          bidCount,
                          minBid: formatBidCurrency(
                            nextMinBid,
                            bidCurrencyCode
                          ),
                        }
                      )}
                    </BidInfoLine>
                  )}
                  {bidBasis === 'RELATIVE' ? (
                    <PercentageBidNote>
                      <FormattedMessage id="ManifestSummaryPopup.percentageBidNote" />
                    </PercentageBidNote>
                  ) : (
                    <>
                      <BidRow>
                        {!isBuyNowPhase && (
                          <BidInputWrapper>
                            <BidInputPrefix>{currencySymbol}</BidInputPrefix>
                            <BidInputField
                              type="number"
                              min={nextMinBid}
                              step={bidPrecision}
                              value={bidInput}
                              disabled={isBuyNowPhase}
                              placeholder={
                                nextMinBid === 0
                                  ? intl.formatMessage(
                                      {
                                        id: 'ManifestSummaryPopup.bidPlaceholder',
                                      },
                                      {
                                        amount: formatBidCurrency(
                                          nextMinBid,
                                          bidCurrencyCode
                                        ),
                                      }
                                    )
                                  : undefined
                              }
                              onChange={(e) => setBidInput(e.target.value)}
                              aria-label={intl.formatMessage({
                                id: 'ManifestSummaryPopup.bidInputLabel',
                              })}
                            />
                          </BidInputWrapper>
                        )}
                        <PlaceBidButton
                          type="button"
                          appearance="primary"
                          size="medium"
                          disabled={!isBidValid}
                          onClick={handlePlaceBid}
                        >
                          <FontAwesomeIcon
                            icon={isBuyNowPhase ? faBolt : faGavel}
                          />
                          <FormattedMessage
                            id={
                              !personId
                                ? isBuyNowPhase
                                  ? 'ManifestSummaryPopup.signInToBuyNow'
                                  : 'ManifestSummaryPopup.signInToBid'
                                : isBuyNowPhase
                                  ? 'ManifestSummaryPopup.buyNow'
                                  : 'ManifestSummaryPopup.placeBid'
                            }
                          />
                        </PlaceBidButton>
                      </BidRow>
                      {showBidError && (
                        <BidInputError>
                          <FormattedMessage
                            id="ManifestSummaryPopup.bidError"
                            values={{
                              amount: formatBidCurrency(
                                nextMinBid,
                                bidCurrencyCode
                              ),
                            }}
                          />
                        </BidInputError>
                      )}
                      {showBidPrecisionError && (
                        <BidInputError>
                          <FormattedMessage id="ManifestSummaryPopup.bidPrecisionError" />
                        </BidInputError>
                      )}
                    </>
                  )}
                </>
              )}
            <BottomButtons>
              {listing.listingId && listing.endTime && (
                <WatchlistPopupCTA
                  listingId={listing.listingId}
                  endTime={listing.endTime}
                  isWatchlisted={isWatchlisted}
                  currency={listing.currency}
                />
              )}
              <SeeListingButton
                href={listingUrl}
                target="_blank"
                rel="noopener noreferrer"
                appearance={bidBasis === 'RELATIVE' ? 'primary' : 'secondary'}
                size="medium"
                onClick={() =>
                  trackButtonClicked({
                    buttonName: 'manifest_view_listing',
                    listing_id: listing.listingId,
                    screenName: 'manifest_summary_popup',
                  })
                }
              >
                <FormattedMessage id="ManifestSummaryPopup.viewListing" />
              </SeeListingButton>
            </BottomButtons>
          </ActionBar>
        )}
      </SidebarContent>
    </SidebarModal>
  )
}

export default ManifestSummaryPopup
